import { DateTime } from 'luxon';
import {
  getValueBgClass,
  getCellStatusClassRules,
  formatUpsideDownside,
  getCellStatusBackgroundByActionCompany,
  formatPriceChange,
  formatRatingsChange,
  formatActionCompany,
  formatLongPrice,
} from '../lib/utils';
import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { Ratings } from '@benzinga/calendar-manager';

export const priceFilterPresets = [
  {
    label: 'Under $5',
    rules: (value: number) => {
      return !!value && value <= 5;
    },
  },
  {
    label: 'Under $10',
    rules: (value: number) => {
      return !!value && value <= 10;
    },
  },
  {
    label: 'Under $20',
    rules: (value: number) => {
      return !!value && value <= 20;
    },
  },
  {
    label: 'Under $50',
    rules: (value: number) => {
      return !!value && value <= 50;
    },
  },
  {
    label: 'Over $5',
    rules: (value: number) => {
      return !!value && value >= 5;
    },
  },
  {
    label: 'Over $10',
    rules: (value: number) => {
      return !!value && value >= 10;
    },
  },
  {
    label: 'Over $20',
    rules: (value: number) => {
      return !!value && value >= 20;
    },
  },
  {
    label: 'Over $50',
    rules: (value: number) => {
      return !!value && value >= 50;
    },
  },
  {
    label: '$10 to $20',
    rules: (value: number) => {
      return !!value && value >= 10 && value <= 20;
    },
  },
  {
    label: '$20 to $50',
    rules: (value: number) => {
      return !!value && value >= 20 && value <= 50;
    },
  },
  {
    label: '$50 to $100',
    rules: (value: number) => {
      return !!value && value >= 50 && value <= 100;
    },
  },
];

const defaultFilterParams = {
  newRowsAction: 'keep',
};

const colDef: (ColDef | ColGroupDef)[] = [
  {
    cellClass: 'ticker-cell',
    field: 'date',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
    },
    headerName: 'Date',
    initialWidth: 120,
    minWidth: 100,
    valueFormatter: ({ value }: { value: string }) => {
      return DateTime.fromISO(value).toFormat('MM/dd/yyyy');
    },
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: 'tickerWidgetCell',
    field: 'ticker',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
    },
    headerName: 'Ticker',
    initialWidth: 70,
    minWidth: 60,
  },
  {
    cellClass: 'buy-now',
    cellRenderer: () => {
      return (
        // eslint-disable-next-line react/jsx-no-target-blank
        <a
          className="rounded bg-green-100 !text-green-600 px-1 py-0.5 whitespace-nowrap hover:bg-green-200"
          href="https://www.benzinga.com/go/buy-now-analyst"
          target="_blank"
        >
          Buy Now
        </a>
      );
    },
    field: 'buy_now',
    filterParams: {
      suppressSorting: true,
    },
    headerName: 'Buy Now',
    initialWidth: 100,
    minWidth: 90,
    suppressHeaderMenuButton: true,
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: ({ data }: { data: Ratings & { logo: string } }) => {
      return (
        <div className="company-name-cell">
          <span>{data.name}</span>
          {data.logo && <img alt="" className="bz-ag-table__company-image" loading="lazy" src={data.logo}></img>}
        </div>
      );
    },
    cellRendererParams: {
      hideOnInitialTicker: true,
    },
    field: 'name',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Company',
    initialWidth: 190,
    minWidth: 150,
  },
  {
    cellRendererParams: {
      hideOnInitialTicker: true,
    },
    cellStyle: { fontWeight: 'bold', textAlign: 'left' },
    field: 'current_price',
    filter: 'priceFilter',
    headerName: 'Current price',
    initialWidth: 120,
    minWidth: 100,
    valueFormatter: ({ data }: { data: Ratings }) => formatLongPrice(Number(data?.quote?.lastTradePrice)),
    valueGetter: ({ data }: { data: Ratings }) =>
      data?.quote?.lastTradePrice ? Number(data.quote.lastTradePrice) : null,
  },
  {
    cellClass: ({ value }: { value: number | string }) => getValueBgClass(value),
    cellStyle: { fontWeight: 'bold', textAlign: 'left' },
    field: 'upside_downside',
    filter: 'percentFilter',
    headerName: 'Upside/Downside',
    initialWidth: 120,
    minWidth: 110,
    valueFormatter: ({ value }: { value: number }) => (value ? `${value}%` : '—'),
    valueGetter: ({ data }: { data: Ratings }) =>
      formatUpsideDownside(Number(data?.quote?.last ?? data?.quote?.lastTradePrice), data.pt_current),
  },
  {
    cellClass: 'analyst-firm-cell',
    field: 'analyst',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
    },
    headerName: 'Analyst Firm',
    initialWidth: 170,
    minWidth: 140,
    valueGetter: ({ data }: { data: Ratings }) => data.analyst,
  },
  {
    cellRenderer: ({ data }: { data: Ratings }) => {
      const smartScore = Number(data?.ratings_accuracy?.smart_score) || null;
      const analystName: string = data?.analyst_name;
      const analystId: string = data?.analyst_id;

      if (!analystName) return '—';

      const getBubbleClassName = (smartScore: number) => {
        if (smartScore >= 70) return 'green';
        else if (smartScore >= 50) return 'yellow';
        else if (smartScore >= 30) return 'orange';
        else return 'red';
      };

      const convertToSlug = (value: string) => value.toLowerCase()?.replace(' ', '-');
      const encodedAnalystName = encodeURIComponent(convertToSlug(analystName));
      const href = `/analyst-stock-ratings/analyst/${analystId}/${encodedAnalystName}`;

      return (
        <a className="w-full" href={href}>
          <div className="analyst-name-and-smart-score-cell truncate">
            <span className="bz-ag-table__analyst-name truncate">{analystName}</span>
            {typeof smartScore === 'number' && (
              <span className={`bz-ag-table__analyst-smart-score ${getBubbleClassName(smartScore)}`}>
                {Math.floor(smartScore) ?? '-'}%
              </span>
            )}
          </div>
        </a>
      );
    },
    cellRendererParams: {
      hideOnInitialTicker: true,
    },
    cellStyle: { cursor: 'pointer' },
    comparator: (_valueA, _valueB, nodeA, nodeB) => {
      const smartScoreA = Number(nodeA.data?.ratings_accuracy?.smart_score ?? 0);
      const smartScoreB = Number(nodeB.data?.ratings_accuracy?.smart_score ?? 0);
      if (smartScoreA === smartScoreB) return 0;
      else if (smartScoreA > smartScoreB) return 1;
      return -1;
    },
    field: 'analyst_name',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Analyst & % Accurate',
    initialWidth: 220,
    minWidth: 180,
    //tooltipShowDelay: 1,
    tooltipValueGetter: params => {
      const analystName = params?.data?.analyst_name;
      const smartScore = params?.data?.ratings_accuracy?.smart_score;
      return analystName && !isNaN(smartScore)
        ? `${analystName} is correct ${Math.floor(smartScore) ?? '-'}% of the time`
        : '';
    },
  },
  {
    cellClass: ({ data }: { data: Ratings }) => getCellStatusClassRules(data.action_pt),
    cellStyle: { fontWeight: 'bold', textAlign: 'left' },
    field: 'pt_prior',
    filter: 'priceFilter',
    filterParams: {
      suppressSorting: false,
      values: priceFilterPresets,
      ...defaultFilterParams,
    },
    headerName: 'Price Target Change',
    initialWidth: 150,
    minWidth: 130,
    valueFormatter: ({ data }: { data: Ratings }) => formatPriceChange(data.pt_prior, data.pt_current),
    valueGetter: ({ data }: { data: Ratings }) => (data.pt_current ? Number(data.pt_current) : null),
  },
  {
    cellClass: ({ data }: { data: Ratings }) => getCellStatusBackgroundByActionCompany(data.action_company || ''),
    cellStyle: { fontWeight: 'bold', textAlign: 'left' },
    field: 'action_company',
    filter: 'agSetColumnFilter',
    filterParams: {
      suppressSorting: true,
      ...defaultFilterParams,
    },
    headerName: 'Rating Change',
    initialWidth: 106,
    minWidth: 100,
    valueFormatter: ({ value }: { value: string }) => formatActionCompany(value),
  },
  // {
  //   cellStyle: { textAlign: 'left', fontWeight: 'bold' },
  //   field: 'rating_prior',
  //   filter: 'agSetColumnFilter',
  //   headerName: 'Previous Rating',
  //   initialWidth: 120,
  //   cellClass: ({data}) => getCellStatusBackgroundByActionCompany(data.action_company)
  // },
  {
    cellClass: ({ data }: { data: Ratings }) => getCellStatusBackgroundByActionCompany(data.action_company ?? ''),
    cellStyle: { fontWeight: 'bold', textAlign: 'left' },
    field: 'rating_current',
    filter: 'agSetColumnFilter',
    filterParams: {
      suppressSorting: true,
      ...defaultFilterParams,
    },
    headerName: 'Previous / Current Rating',
    initialWidth: 190,
    minWidth: 170,
    valueFormatter: ({ data }: { data: Ratings }) =>
      formatRatingsChange(data.rating_prior, data.rating_current, data.action_company ?? ''),
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: () => {
      return (
        // eslint-disable-next-line react/jsx-no-target-blank
        <a href="https://www.benzinga.com/profile/portfolio/?action=login&utm_source=calendars" target="_blank">
          Get Alert
        </a>
      );
    },
    field: 'get_alert',
    headerName: 'Get Alert',
    initialWidth: 70,
    minWidth: 70,
    suppressAutoSize: true,
    suppressHeaderMenuButton: true,
    suppressSizeToFit: true,
  },
];

export default colDef;
